import * as React from 'react';
import {
  type CaptionLabelProps,
  type Locale,
  type MonthGridProps,
} from 'react-day-picker';
import { ChevronDownIcon } from 'lucide-react';

import { cn } from 'cn';
import {
  buildMonthGridItems,
  buildYearGridItems,
  type CalendarDrilldownView,
  type CalendarOptionGridItem,
} from './date-time-utils';

// Shared grid used by both the drilldown month view (12 months) and year view (12-year
// decade block) — same cell sizing/spacing and selected/today styling either way.
export function CalendarOptionGrid({
  items,
  onSelect,
}: {
  items: CalendarOptionGridItem[];
  onSelect: (key: number) => void;
}) {
  return (
    <div className="tw:flex tw:w-[calc(var(--cell-size)*7)] tw:flex-wrap tw:gap-2">
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          onClick={() => onSelect(item.key)}
          className={cn(
            'tw:flex tw:h-12 tw:basis-[calc((100%-1.5rem)/4)] tw:items-center tw:justify-center tw:rounded-md tw:text-sm tw:font-medium tw:transition-colors tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50',
            item.isSelected
              ? 'tw:bg-primary tw:text-primary-foreground'
              : item.isCurrent
                ? 'tw:bg-accent tw:text-accent-foreground'
                : 'tw:text-foreground tw:hover:bg-accent tw:hover:text-accent-foreground'
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

type CalendarDrilldownComponents = {
  CaptionLabel: (props: CaptionLabelProps) => React.ReactNode;
  MonthGrid: (props: MonthGridProps) => React.ReactNode;
  PreviousMonthButton: (
    props: React.ComponentProps<'button'>
  ) => React.ReactNode;
  NextMonthButton: (props: React.ComponentProps<'button'>) => React.ReactNode;
};

// Backs the 'drilldown' navigationVariant in ui/calendar.tsx: lets the caption label be
// clicked to drill into a 12-month grid, then a 12-year decade grid, and back out again,
// instead of only ever stepping one month at a time via the default prev/next buttons.
export function useCalendarDrilldown(params: {
  navigationVariant: 'standard' | 'drilldown';
  month: Date | undefined;
  defaultMonth: Date | undefined;
  onMonthChange: ((date: Date) => void) | undefined;
  selectedDate: Date | undefined;
  locale: Partial<Locale> | undefined;
}): {
  isDrilldown: boolean;
  displayMonth: Date;
  handleMonthChange: (nextMonth: Date) => void;
  components: CalendarDrilldownComponents | undefined;
} {
  const {
    navigationVariant,
    month,
    defaultMonth,
    onMonthChange,
    selectedDate,
    locale,
  } = params;
  const isDrilldown = navigationVariant === 'drilldown';

  // Only the 'drilldown' variant needs a locally-owned displayed month: it must be
  // settable from the custom month/year grids below, not just DayPicker's own nav.
  const [view, setView] = React.useState<CalendarDrilldownView>('days');
  const [internalMonth, setInternalMonth] = React.useState<Date>(
    month ?? defaultMonth ?? new Date()
  );
  const displayMonth = month ?? internalMonth;

  const handleMonthChange = React.useCallback(
    (nextMonth: Date) => {
      setInternalMonth(nextMonth);
      onMonthChange?.(nextMonth);
    },
    [onMonthChange]
  );

  const stepYears = React.useCallback(
    (years: number) => {
      const next = new Date(displayMonth);
      next.setFullYear(next.getFullYear() + years);
      handleMonthChange(next);
    },
    [displayMonth, handleMonthChange]
  );

  const today = React.useMemo(() => new Date(), []);
  const decadeStart = Math.floor(displayMonth.getFullYear() / 10) * 10;

  // Shared prev/next nav button for drilldown mode: steps a month at the 'days' level
  // (default DayPicker behavior) or a year/decade at the 'months'/'years' levels.
  const makeDrilldownNavButton = React.useCallback(
    (direction: 1 | -1) => {
      function DrilldownNavButton({
        onClick,
        'aria-disabled': ariaDisabled,
        tabIndex,
        ...btnProps
      }: React.ComponentProps<'button'>) {
        return (
          <button
            type="button"
            aria-disabled={view === 'days' ? ariaDisabled : undefined}
            tabIndex={view === 'days' ? tabIndex : undefined}
            onClick={(event) => {
              if (view === 'days') {
                onClick?.(event);
                return;
              }
              event.preventDefault();
              stepYears(direction * (view === 'months' ? 1 : 12));
            }}
            {...btnProps}
          />
        );
      }
      return DrilldownNavButton;
    },
    [view, stepYears]
  );

  // Memoized so DayPicker doesn't see a new component type (and remount these slots) on
  // every render — `view` changes are exactly what drive re-renders in drilldown mode.
  const previousMonthButton = React.useMemo(
    () => makeDrilldownNavButton(-1),
    [makeDrilldownNavButton]
  );
  const nextMonthButton = React.useMemo(
    () => makeDrilldownNavButton(1),
    [makeDrilldownNavButton]
  );

  const captionLabelOverride = React.useCallback(
    ({ className, children, ...spanProps }: CaptionLabelProps) => {
      const label =
        view === 'days'
          ? children
          : view === 'months'
            ? String(displayMonth.getFullYear())
            : `${decadeStart}\u2013${decadeStart + 11}`;

      if (view === 'years') {
        return (
          <span className={className} {...spanProps}>
            {label}
          </span>
        );
      }

      return (
        <button
          type="button"
          className={className}
          onClick={() => setView(view === 'days' ? 'months' : 'years')}
        >
          {label}
          <ChevronDownIcon className="tw:size-3.5 tw:text-muted-foreground" />
        </button>
      );
    },
    [view, displayMonth, decadeStart]
  );

  const monthGridOverride = React.useCallback(
    ({ children, className, ...gridProps }: MonthGridProps) => {
      if (view === 'months') {
        return (
          <div className={className}>
            <CalendarOptionGrid
              items={buildMonthGridItems(
                displayMonth.getFullYear(),
                selectedDate,
                today,
                locale
              )}
              onSelect={(monthIndex) => {
                handleMonthChange(
                  new Date(displayMonth.getFullYear(), monthIndex, 1)
                );
                setView('days');
              }}
            />
          </div>
        );
      }

      if (view === 'years') {
        return (
          <div className={className}>
            <CalendarOptionGrid
              items={buildYearGridItems(decadeStart, selectedDate, today)}
              onSelect={(year) => {
                handleMonthChange(new Date(year, displayMonth.getMonth(), 1));
                setView('months');
              }}
            />
          </div>
        );
      }

      return (
        <table className={className} {...gridProps}>
          {children}
        </table>
      );
    },
    [
      view,
      displayMonth,
      selectedDate,
      today,
      locale,
      decadeStart,
      handleMonthChange,
    ]
  );

  const components = React.useMemo<CalendarDrilldownComponents | undefined>(
    () =>
      isDrilldown
        ? {
            CaptionLabel: captionLabelOverride,
            MonthGrid: monthGridOverride,
            PreviousMonthButton: previousMonthButton,
            NextMonthButton: nextMonthButton,
          }
        : undefined,
    [
      isDrilldown,
      captionLabelOverride,
      monthGridOverride,
      previousMonthButton,
      nextMonthButton,
    ]
  );

  return { isDrilldown, displayMonth, handleMonthChange, components };
}
